import { shipmentOrder } from './shipment-order.ts';
import { contains } from './derive.ts';
import type { AlertEvent, AssetType, DeviceModel, Id, NotificationDelivery, PlaceKind, RuleType, ShipmentStatus, TimeRange, WorkspaceRepository } from './types.ts';
export interface DeviceFilters { search?: string; groupId?: Id; model?: DeviceModel; assetType?: AssetType; placeId?: Id; notSeenSince?: number }
export interface AlertFilters { status?: AlertEvent['status']; assetId?: Id; ruleId?: Id; type?: RuleType; placeId?: Id; range?: TimeRange; limit?: number }
export interface ShipmentFilters { status?: ShipmentStatus; assetId?: Id; search?: string }
export interface ShareFilters { status?: 'active' | 'expired' | 'revoked'; assetId?: Id; shipmentId?: Id }
const inRange = (at: number, range?: TimeRange) => !range || (at >= range.from && at < range.to);
const overlaps = (start: number, end: number | null, range?: TimeRange) => !range || (start < range.to && (end ?? Infinity) >= range.from);
/** All methods are scoped to this repository. Unknown or cross-workspace IDs return undefined/empty. */
export function createWorkspaceQueries(repository: WorkspaceRepository) {
  const d = repository.data;
  const assets = new Map(d.assets.map(a => [a.id, a]));
  const devices = new Map(d.devices.map(a => [a.id, a]));
  const assetByDevice = new Map(d.assets.map(a => [a.deviceId, a]));
  const places = new Map(d.places.map(a => [a.id, a]));
  const latest = new Map(d.latestFixes.map(f => [f.assetId, f]));
  const rules = new Map(d.rules.map(r => [r.id, r]));
  const shipments = new Map(d.shipments.map(s => [s.id, s]));
  return {
    workspace: d.workspace,
    now: d.now,
    historyFrom: d.historyFrom,
    listDevices(filters: DeviceFilters = {}) {
      const search = filters.search?.trim().toLowerCase();
      return d.devices.filter(device => {
        const asset = assetByDevice.get(device.id);
        const fix = asset && latest.get(asset.id);
        const place = filters.placeId && places.get(filters.placeId);
        return (!search || [device.name, device.imei, asset?.name, asset?.inventoryNumber, asset?.vin].some(s => s?.toLowerCase().includes(search)))
          && (!filters.groupId || device.groupId === filters.groupId)
          && (!filters.model || device.model === filters.model)
          && (!filters.assetType || asset?.type === filters.assetType)
          && (!filters.placeId || !!(place && fix && contains(place, fix)))
          && (filters.notSeenSince === undefined || !fix || fix.seenAt <= filters.notSeenSince);
      });
    },
    getDevice: (id: Id) => devices.get(id),
    listAssets: (type?: AssetType) => d.assets.filter(a => !type || a.type === type),
    getAsset: (id: Id) => assets.get(id),
    getAssetForDevice: (deviceId: Id) => assetByDevice.get(deviceId),
    getLatestLocation: (assetId: Id) => latest.get(assetId),
    getLastSeenPlace(assetId: Id) { const fix = latest.get(assetId); return fix ? d.places.find(p => contains(p, fix)) : undefined; },
    getLocationHistory: (assetId: Id, range?: TimeRange) => repository.getHistory(assetId).fixes.filter(f => inRange(f.seenAt, range)),
    getTrips: (assetId: Id, range?: TimeRange) => repository.getHistory(assetId).trips.filter(t => overlaps(t.startedAt, t.endedAt, range)),
    getStops: (assetId: Id, range?: TimeRange) => repository.getHistory(assetId).stops.filter(s => overlaps(s.arrivedAt, s.leftAt ?? s.lastSeenAt, range)),
    getSatelliteCheckIns: (assetId: Id, range?: TimeRange) => repository.getHistory(assetId).satelliteCheckIns.filter(c => inRange(c.seenAt, range)),
    listAlerts(filters: AlertFilters = {}) {
      return d.alerts.filter(a => (!filters.status || a.status === filters.status) && (!filters.assetId || a.assetId === filters.assetId)
        && (!filters.ruleId || a.ruleId === filters.ruleId) && (!filters.type || rules.get(a.ruleId)?.type === filters.type)
        && (!filters.placeId || a.placeId === filters.placeId) && inRange(a.at, filters.range)).slice(0, filters.limit ?? Infinity);
    },
    getAlert: (id: Id) => d.alerts.find(a => a.id === id),
    listNotificationDeliveries: (alertId?: Id, status?: NotificationDelivery['status']) => d.deliveries.filter(n => (!alertId || n.alertId === alertId) && (!status || n.status === status)),
    getRecipient: (id: Id) => d.recipients.find(r => r.id === id),
    listShipments: (filters: ShipmentFilters = {}) => d.shipments.filter(s => (!filters.status || s.status === filters.status) && (!filters.assetId || s.assetIds.includes(filters.assetId))
      && (!filters.search || s.reference.toLowerCase().includes(filters.search.toLowerCase()))).sort(shipmentOrder),
    getShipment: (id: Id) => shipments.get(id),
    getShipmentCheckpoints: (shipmentId: Id) => d.checkpoints.filter(c => c.shipmentId === shipmentId).sort((a, b) => a.sequence - b.sequence),
    listRules: (type?: RuleType) => d.rules.filter(r => !type || r.type === type),
    getRule: (id: Id) => rules.get(id),
    listPlaces: (kind?: PlaceKind) => d.places.filter(p => !kind || p.kind === kind),
    getPlace: (id: Id) => places.get(id),
    listGroups: () => d.groups,
    listGateways: () => d.gateways,
    listShareLinks(filters: ShareFilters = {}) {
      return d.shareLinks.filter(s => {
        const status = s.revokedAt !== null ? 'revoked' : s.expiresAt <= d.now ? 'expired' : 'active';
        return (!filters.status || filters.status === status)
          && (!filters.assetId || (s.scope.kind === 'asset' && s.scope.assetId === filters.assetId))
          && (!filters.shipmentId || (s.scope.kind === 'shipment' && s.scope.shipmentId === filters.shipmentId));
      });
    },
    getShareLink: (id: Id) => d.shareLinks.find(s => s.id === id),
    listMembers: () => d.members,
    getMember: (id: Id) => d.members.find(m => m.id === id),
    getUser: (id: Id) => d.users.find(u => u.id === id),
    listOrders: () => d.orders,
    getOrder: (id: Id) => d.orders.find(o => o.id === id),
    getOrderLines: (orderId: Id) => d.orderLines.filter(l => l.orderId === orderId),
    listAuditEvents: (range?: TimeRange) => d.auditEvents.filter(e => inRange(e.at, range)),
  };
}
export type WorkspaceQueries = ReturnType<typeof createWorkspaceQueries>;
